import { useEffect, useRef, useState } from "react";

import { BAR_COUNT } from "@/constants/conversionShowcase";

// Synthetic bar heights for the output waveform when nothing is playing. Keeps the demo visually alive instead of a flat line; must stay in sync with BAR_COUNT so it swaps cleanly with the real analyser-driven waveform.
export const useIdleWave = (isActive: boolean) => {
  const [idleWave, setIdleWave] = useState<number[]>(
    Array.from({ length: BAR_COUNT }, () => 0)
  );
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      return;
    }

    // Low-frequency sine + harmonics + light jitter: reads as “audio-ish” without matching any real spectrum (that only exists while isPlaying).
    const animate = (timestamp: number) => {
      const t = timestamp / 1000;
      const bars = Array.from({ length: BAR_COUNT }, (_, i) => {
        const phase = (i / BAR_COUNT) * Math.PI * 2;
        const wave = Math.sin(t * 0.8 + phase) * 0.5 + 0.5;
        const harmonic = Math.sin(t * 1.4 + phase * 1.5) * 0.15;
        const noise = Math.sin(t * 7 + i * 2.3) * 0.04;
        return Math.max(0.02, (wave * 0.18 + harmonic + noise) * 0.55);
      });
      setIdleWave(bars);
      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [isActive]);

  return idleWave;
};
